<template>
  <section>
    <v-card class="items-card">
      <v-list v-if="initialized">
        <v-list-group
          v-for="(product, index) in productList"
          :key="index"
          v-model="product.SelectionActive"
          append-icon=""
          @change="onSelectionActiveChange(product)"
        >
          <template v-slot:activator>
            <v-list-item-avatar :width="imgWidth" :height="imgHeight" rounded>
              <v-img
                v-if="
                  product.Product.PictureUrl != null &&
                  product.Product.PictureUrl !== ''
                "
                :src="product.Product.PictureUrl"
              />
              <v-icon v-else>mdi-image-off</v-icon>
              <!-- Highlighting label -->
              <HighlightingLabel
                v-if="product.Item"
                :label="product.Item.HighlightingLabel"
                :visible="product.Item.Highlighted"
              />
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title v-text="product.Product.Name" />
              <v-list-item-subtitle>
                <v-col>
                  <v-row align="baseline">
                    <div
                      v-if="isOnDiscount(product)"
                      class="discount-price text-body-1 mr-1"
                    >
                      {{
                        formatter.format(getProductTotalRetailPrice(product))
                      }}
                    </div>
                    <div
                      class="text-h6"
                      :class="
                        hasGeneralDiscount(product)
                          ? 'red--text'
                          : 'text--primary'
                      "
                    >
                      {{ formatter.format(getProductTotalPrice(product)) }}
                      <span class="text-caption text--secondary">
                        {{ priceSuffix }}
                      </span>
                    </div>
                  </v-row>
                  <v-row v-if="isProfUser" align="baseline">
                    <div class="text-subtitle-2 text--primary">
                      {{
                        formatter.format(
                          getTotalPriceInclVATForProduct(product)
                        )
                      }}
                    </div>
                    <div class="text-caption text--secondary ml-1">
                      {{ getVATText(product.Product.VatPercentage) }}
                    </div>
                  </v-row>
                </v-col>
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn icon>
                <v-icon color="primary">mdi-plus-circle-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>

          <v-list-item
            style="background-color: #eeeeee"
            :value="product"
            :key="`${index}-expansion`"
          >
            <v-col class="px-0">
              <!-- Product options if exist -->
              <v-row class="ma-0" v-if="hasOptions(product.Product)">
                <v-col
                  cols="12"
                  class="ma-0"
                  v-for="(list, index) in product.Product.OptionLists"
                  :key="index"
                >
                  <ProductOptions
                    :selectedProduct="list"
                    class="mb-4"
                    @validateOptionSelection="
                      (invalid) => onValidateOptionSelection(product, invalid)
                    "
                  />
                </v-col>
              </v-row>

              <!-- Variations selection if available -->
              <v-row
                v-if="
                  product.Product.ItemDefs != null &&
                  Object.keys(product.Product.ItemDefs).length > 0
                "
                class="ma-0 mb-1"
                justify="space-between"
              >
                <v-col
                  v-for="(item, itemName) in product.Product.ItemDefs"
                  cols="12"
                  md="6"
                  class="pb-2 pt-0 px-2"
                  :key="itemName"
                >
                  <v-select
                    v-model="product.ItemDefObj[itemName]"
                    :items="getSortedItemDefValues(product.Product, itemName)"
                    :label="itemName"
                    hide-details="auto"
                    dense
                    filled
                    background-color="white"
                    @input="onItemDefChange(product)"
                  />
                </v-col>
              </v-row>

              <!-- Out of stock alert -->
              <v-alert v-if="outOfStock(product)" type="error" dense>
                {{ $translate('OUT_OF_STOCK') }}
              </v-alert>

              <!-- Quantity operations -->
              <v-row class="ma-0 mb-3" align="center">
                <h4>{{ $translate('AMOUNT_OF_PRODUCTS') }}:</h4>
                <v-spacer />
                <v-btn-toggle rounded>
                  <v-btn
                    small
                    :disabled="decreaseBtnDisabled(product)"
                    @click="decreaseQuantity(product)"
                  >
                    <v-icon small>ti-minus</v-icon>
                  </v-btn>
                  <v-card
                    height="28"
                    min-width="60"
                    flat
                    outlined
                    tile
                    class="text-center px-3"
                  >
                    {{ getDisplayQuantity(product.Product, product.Quantity) }}
                    <span v-if="isWeightedProduct(product)">
                      {{ getDisplayUnit(product.Product) }}
                    </span>
                  </v-card>
                  <v-btn
                    small
                    :disabled="increaseBtnDisabled(product)"
                    @click="increaseQuantity(product)"
                  >
                    <v-icon small>ti-plus</v-icon>
                  </v-btn>
                </v-btn-toggle>
              </v-row>

              <v-row class="ma-0" justify="end">
                <v-btn
                  color="primary"
                  depressed
                  class="text-capitalize primary-font-color"
                  :disabled="bookBtnDisabled(product)"
                  @click="onBookBtnClick(product)"
                >
                  {{ $translate('ADD') }}
                </v-btn>
              </v-row>
            </v-col>
          </v-list-item>

          <v-divider v-if="index < productList.length - 1" :key="index" />
        </v-list-group>
      </v-list>
    </v-card>
  </section>
</template>

<script>
/* eslint-disable no-param-reassign */
import {
  applyPersonalDiscount,
  getDisplayQuantity,
  getDisplayUnit,
  getItemByDefs,
  getQuantity,
  getSortedItemDefValues,
  getTotalPriceExclVAT,
  getTotalPriceInclVAT,
  getTotalRetailPriceExclVAT,
  getTotalRetailPriceInclVAT,
  getVATText,
  hasCapacity,
  hasGeneralDiscount,
  hasOptions,
  isOnDiscount,
  personalDiscount,
  quantityDecreasable,
  quantityIncreasable,
  toOptionSummary,
} from '@/objects/Product';
import utility from '@/shared/utility';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';
import ProductOptions from '@/components/event/productDetails/ProductOptions';

export default {
  name: 'SubscriptionProductsAdd',
  props: {
    products: Array,
  },
  components: {
    HighlightingLabel,
    ProductOptions,
  },
  data() {
    return {
      productList: [],
      initialized: false,
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  computed: {
    imgWidth() {
      const breakPontObj = this.$vuetify.breakpoint;

      switch (breakPontObj.name) {
        case 'xs':
          return 80;
        case 'sm':
          return 100;
        case 'md':
          return 130;
        case 'lg':
        case 'xl':
        default:
          return 150;
      }
    },
    imgHeight() {
      return this.imgWidth * 0.618;
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    priceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    partnerUserRelations() {
      return this.$store.getters['auth/partnerUserRelations'];
    },
  },
  watch: {
    products() {
      this.initProducts();
    },
  },
  mounted() {
    this.initProducts();
    this.initialized = true;
  },
  methods: {
    initProducts() {
      this.productList = [];

      if (this.products != null) {
        this.productList = [...this.products];
      }

      this.initSelections();
    },
    initSelections() {
      // eslint-disable-next-line no-restricted-syntax
      for (const product of this.productList) {
        this.initProductSelection(product);
      }
    },
    initProductSelection(product) {
      if (product.Product.Items != null && product.Product.Items.length > 0) {
        const item = { ...product.Product.Items[0] };
        const itemDefs = { ...item.ItemDef };
        const itemDefsStr = this.getItemDefsStr(itemDefs);
        const itemId = item.Id;

        product.ItemDefObj = itemDefs;
        product.ItemDef = itemDefsStr;
        product.Item = item;
        product.ItemId = itemId;
        product.Capacity = item.Capacity;

        product.Quantity = getQuantity(
          product.Product,
          product.Item,
          product.Quantity
        );

        product.Price = this.getTotalPriceInclVATForProduct(product);
      }
    },
    isWeightedProduct(product) {
      if (
        product != null &&
        product.Product != null &&
        product.Product.Type === 'Weighted'
      ) {
        return true;
      }
      return false;
    },
    increaseBtnDisabled(product) {
      return !quantityIncreasable(
        product.Product,
        product.Item,
        product.Quantity
      );
    },
    decreaseBtnDisabled(product) {
      return !quantityDecreasable(
        product.Product,
        product.Item,
        product.Quantity
      );
    },
    increaseQuantity(product) {
      if (this.isWeightedProduct(product)) {
        product.Quantity += product.Product.WeightedInfo.StepSize;
      } else {
        product.Quantity += 1;
      }
    },
    decreaseQuantity(product) {
      if (this.isWeightedProduct(product)) {
        product.Quantity -= product.Product.WeightedInfo.StepSize;
      } else {
        product.Quantity -= 1;
      }
    },
    updateTotalPrice(product) {
      // eslint-disable-next-line no-param-reassign
      product.Price = this.getTotalPriceInclVATForProduct(product);
    },
    getProductTotalPrice(product) {
      let price = 0;
      if (applyPersonalDiscount(product)) {
        price = this.getTotalPriceWithPersonalDiscount(product);
      } else {
        price = this.getTotalPriceWithoutPersonalDiscount(product);
      }
      return price;
    },
    getProductTotalRetailPrice(product) {
      if (applyPersonalDiscount(product)) {
        return this.getTotalRetailPriceWithPersonalDiscount(product);
      }
      return this.getTotalRetailPriceWithoutPersonalDiscount(product);
    },
    getTotalPriceInclVATForProduct(product) {
      if (applyPersonalDiscount(product)) {
        const priceInclVat = getTotalPriceInclVAT(product);
        return utility.getDiscountPrice(
          priceInclVat,
          personalDiscount(product)
        );
      }
      return getTotalPriceInclVAT(product);
    },
    getTotalPriceWithPersonalDiscount(product) {
      let price = this.getTotalPriceWithoutPersonalDiscount(product);
      if (price != null && applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(price, personalDiscount(product));
      }
      return price;
    },
    getTotalPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return getTotalPriceExclVAT(product);
      }
      return getTotalPriceInclVAT(product);
    },
    getTotalRetailPriceWithPersonalDiscount(product) {
      if (applyPersonalDiscount(product)) {
        return this.getTotalPriceWithoutPersonalDiscount(product);
      }
      return null;
    },
    getTotalRetailPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return getTotalRetailPriceExclVAT(product);
      }
      return getTotalRetailPriceInclVAT(product);
    },
    itemBookable(product) {
      if (
        product == null ||
        product.Product == null ||
        product.ItemId == null ||
        product.ItemId === '' ||
        product.Quantity <= 0
      ) {
        return false;
      }

      return true;
    },
    bookBtnDisabled(product) {
      if (!this.itemBookable(product)) {
        // Product not bookable
        return true;
      }

      return false;
    },
    onItemDefChange(product) {
      const defs = product.ItemDefObj;
      const defsStr = this.getItemDefsStr(defs);
      const item = this.getItem(product, defs);
      product.ItemDef = defsStr;
      product.Item = item;
      product.ItemId = item.Id;
      product.Capacity = item.Capacity;
      product.Quantity = getQuantity(
        product.Product,
        product.Item,
        product.Quantity
      );
    },
    onBookBtnClick(product) {
      if (hasOptions(product.Product) && !product.OptionSelectionValid) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('PRODUCT_OPTIONS_ERROR'),
          color: 'warning',
        });
        return;
      }
      product.OptionSelection = this.getProductOptions(product);
      product.OptionSelectionSummary = toOptionSummary(product.OptionSelection);
      this.updateTotalPrice(product);
      // Create a deep copy of the product to avoid reference issues
      const productCopy = JSON.parse(JSON.stringify(product));
      productCopy.ProductId = product.Product.ProductId || product.Product.Id;
      this.$emit('add', productCopy);

      // Close selection and init selection
      product.SelectionActive = false;
      this.onSelectionActiveChange(product);
    },
    getItemDefsStr(defs) {
      if (defs == null || Object.keys(defs).length === 0) {
        // No variations selected or selection not completed yet
        return '';
      }

      let defsStr = '';
      Object.entries(defs).forEach(([key, value]) => {
        defsStr += `${key}: ${value}, `;
      });

      // Remove last comma
      return defsStr.replace(/,\s*$/, '');
    },
    getItem(product, defs) {
      if (product == null || product.Product == null) {
        return {};
      }
      return getItemByDefs(product.Product, defs);
    },
    outOfStock(product) {
      return !hasCapacity(product.Product, product.Item);
    },
    onSelectionActiveChange(product) {
      // Init product
      this.initProductSelection(product);
    },
    onValidateOptionSelection(product, invalid) {
      product.OptionSelectionValid = !invalid;
    },
    getProductOptions(product) {
      if (hasOptions(product.Product)) {
        let options = product.Product.OptionLists[0].OptionGroups[0].Options;
        let optionSelection = [];
        for (const key in options) {
          let quantity =
            options[key].MaxQuantity > 1 || options[key].MaxQuantity == -1
              ? options[key].Quantity
              : options[key].Selected
              ? 1
              : 0;
          if (quantity > 0) {
            let optSelection = {
              OptionId: options[key].OptionId,
              OptionName: options[key].OptionName,
              Quantity: quantity,
              TextResponse: '',
              PictureResponse: '',
            };
            optionSelection.push(optSelection);
          }
        }
        return optionSelection;
      }
      return [];
    },
    getSortedItemDefValues,
    getVATText,
    isOnDiscount,
    hasGeneralDiscount,
    hasOptions,
    getDisplayUnit,
    getDisplayQuantity,
  },
};
</script>

<style scoped lang="scss">
.items-card {
  width: 100%;
  min-height: 30em;
  max-height: 30em;
  overflow-x: auto;
  overflow-y: auto;
}
</style>
